'use client';

import { useCallback, useMemo } from 'react';
const LEFT_PAGE = 'LEFT';
const RIGHT_PAGE = 'RIGHT';
const range = (from, to, step = 1) => {
  let i = from;
  const range = [];
  while (i <= to) {
    range.push(i.toString());
    i += step;
  }
  return range;
};
const usePagination = (count, currentPage, limit, onPage, window = 1) => {
  const totalPages = useMemo(() => Math.ceil(count / limit), [count, limit]);
  const gotoPage = useCallback(page => {
    const currentPage_0 = Math.max(0, Math.min(page, totalPages));
    onPage(currentPage_0);
  }, [onPage, totalPages]);
  const handleClick = useCallback(page_0 => gotoPage(page_0), [gotoPage]);
  const handleMoveLeft = useCallback(() => {
    gotoPage(currentPage - window * 2 - 1);
  }, [currentPage, gotoPage, window]);
  const handleMoveRight = useCallback(() => {
    gotoPage(currentPage + window * 2 + 1);
  }, [currentPage, gotoPage, window]);
  const pages_0 = useMemo(() => {
    /**
     * totalNumbers: the total page numbers to show on the control
     * totalBlocks: totalNumbers + 2 to cover for the left(<) and right(>) controls
     */
    const totalNumbers = window * 2 + 3;
    const totalBlocks = totalNumbers + 2;
    if (totalPages > totalBlocks) {
      const startPage = Math.max(2, currentPage - window);
      const endPage = Math.min(totalPages - 1, currentPage + window);
      let pages = range(startPage, endPage);
      /**
       * hasLeftSpill: has hidden pages to the left
       * hasRightSpill: has hidden pages to the right
       * spillOffset: number of hidden pages either to the left or to the right
       */
      const hasLeftSpill = startPage > 2;
      const hasRightSpill = totalPages - endPage > 1;
      const spillOffset = totalNumbers - (pages.length + 1);
      switch (true) {
        // handle: (1) < {5 6} [7] {8 9} (10)
        case hasLeftSpill && !hasRightSpill:
          {
            const extraPages_0 = range(startPage - spillOffset, startPage - 1);
            pages = [LEFT_PAGE, ...extraPages_0, ...pages];
            break;
          }
        // handle: (1) {2 3} [4] {5 6} > (10)
        case !hasLeftSpill && hasRightSpill:
          {
            const extraPages = range(endPage + 1, endPage + spillOffset);
            pages = [...pages, ...extraPages, RIGHT_PAGE];
            break;
          }
        // handle: (1) < {4 5} [6] {7 8} > (10)
        case hasLeftSpill && hasRightSpill:
        default:
          {
            pages = [LEFT_PAGE, ...pages, RIGHT_PAGE];
            break;
          }
      }
      return ['1', ...pages, totalPages.toString()];
    }
    return range(1, totalPages);
  }, [currentPage, totalPages, window]);
  const actionablePages = useMemo(() => pages_0.map(page_1 => {
    if (page_1 === LEFT_PAGE) return {
      action: handleMoveLeft,
      label: '<'
    };
    if (page_1 === RIGHT_PAGE) return {
      action: handleMoveRight,
      label: '>'
    };
    return {
      action: () => handleClick(parseInt(page_1, 10)),
      label: page_1
    };
  }), [handleClick, handleMoveLeft, handleMoveRight, pages_0]);
  return actionablePages;
};
export default usePagination;